import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import momentPropTypes from 'react-moment-proptypes'

const DatePickerMonthSelector = ({
    date,
    month,
    onMonthSelect,
    onYearSelect,
    isOutsideRange,
    disablePastDates,
    disableFutureDates,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
}) => {
    const [isYearDisabled, setIsYearDisabled] = useState(false)

    useEffect(() => {
        // Reset year disabled state when month changes
        setIsYearDisabled(false)
    }, [month])

    const getStartAndEndYears = () => {
        const currentYear = moment().year()
        const start = disablePastDates ? currentYear : currentYear - 50
        const end = disableFutureDates ? currentYear : currentYear + 4
        return { start, end }
    }

    const handleDecreaseMonth = () => {
        const newMonth = moment(month).subtract(1, 'month')
        const { start, end } = getStartAndEndYears()

        if (newMonth.year() < start || newMonth.year() > end) {
            return // Out of range, do nothing
        }

        decreaseMonth()
        onMonthSelect(newMonth, newMonth.month())
    }

    const handleIncreaseMonth = () => {
        const newMonth = moment(month).add(1, 'month')
        const { start, end } = getStartAndEndYears()

        if (newMonth.year() < start || newMonth.year() > end) {
            return // Out of range, do nothing
        }

        increaseMonth()
        onMonthSelect(newMonth, newMonth.month())
    }

    const returnYears = () => {
        const years = []
        let start = disablePastDates ? moment().year() : moment().year() - 50
        let end = disableFutureDates ? moment().year() : moment().year() + 4

        // make sure the year range includes currently selected year
        const selectedYear = date ? moment(date).year() : moment().year()
        start = Math.min(selectedYear, start)
        end = Math.max(selectedYear, end)

        for (let i = start; i <= end; i++) {
            years.push(<option key={i} value={i}>{i}</option>)
        }

        return years.reverse()
    }

    return (
        <div>
            <button
                type="button"
                className="react-datepicker__navigation react-datepicker__navigation--previous"
                aria-label="Previous Month"
                onClick={handleDecreaseMonth}
                disabled={prevMonthButtonDisabled}
            >
                <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">
                    Previous Month
                </span>
            </button>
            <button
                type="button"
                className="react-datepicker__navigation react-datepicker__navigation--next"
                aria-label="Next Month"
                onClick={handleIncreaseMonth}
                disabled={nextMonthButtonDisabled}
            >
                <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">
                    Next Month
                </span>
            </button>
            <div className="react-datepicker__month-container">
                <div className="react-datepicker__header">
                    <h2 className="react-datepicker__current-month react-datepicker__current-month--hasYearDropdown react-datepicker__current-month--hasMonthDropdown">
                        {month.format('MMMM YYYY')}
                    </h2>
                    <div className="react-datepicker__header__dropdown react-datepicker__header__dropdown--select" style={{ display: 'contents' }}>
                        <div
                            className="react-datepicker__month-dropdown-container react-datepicker__month-dropdown-container--select"
                        >
                            <select
                                className="react-datepicker__month-select"
                                value={month.month()}
                                onChange={(e) => onMonthSelect(month, Number(e.target.value))}
                            >
                                {moment.months().map((label, value) => (
                                    <option
                                        key={value}
                                        value={value}
                                        disabled={isOutsideRange(moment().set({ year: month.year(), month: value }))}
                                    >
                                        {label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div
                        className="react-datepicker__year-dropdown-container react-datepicker__year-dropdown-container--select"
                    >
                        <select
                            className="react-datepicker__year-select"
                            value={month.year()}
                            onChange={(e) => onYearSelect(month, Number(e.target.value))}
                            disabled={isYearDisabled}
                        >
                            {returnYears()}
                        </select>
                    </div>
                </div>
            </div>
        </div>
    )
}

DatePickerMonthSelector.propTypes = {
    date: momentPropTypes.momentObj,
    month: momentPropTypes.momentObj,
    onYearSelect: PropTypes.func.isRequired,
    onMonthSelect: PropTypes.func.isRequired,
    isOutsideRange: PropTypes.func.isRequired,
    disablePastDates: PropTypes.bool,
    disableFutureDates: PropTypes.bool,
    decreaseMonth: PropTypes.func.isRequired,
    increaseMonth: PropTypes.func.isRequired,
    prevMonthButtonDisabled: PropTypes.bool,
    nextMonthButtonDisabled: PropTypes.bool,
}

export default DatePickerMonthSelector
